import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { useShowErrorSnackbar } from '@/common/application/hooks/useShowErrorSnackbar';
import { extractRestApiErrorMessage } from '@/common/application/utils/restApiErrorUtils';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import { raptorJobApiService } from '@/raptorjob/infrastructure/rest/raptorJobApi';
import type { RaptorJobDto } from '@/libs/api-rest/generated/model';

import {
  useGetApiV3RaptorJobs,
  useGetApiV3RaptorJobsId,
  usePutApiV3RaptorJobsId,
  usePatchApiV3RaptorJobsIdUpdateStatus,
  usePostApiV3RaptorJobsCreateSingleDayJob,
  getGetApiV3RaptorJobsQueryKey,
  getGetApiV3RaptorJobsIdQueryKey
} from '@/libs/api-rest/generated/raptor-jobs/raptor-jobs';
import { getGetApiV3RaptorJobsJobIdTicketsQueryKey } from '@/libs/api-rest/generated/tickets/tickets';
import { useMapRaptorJobPayload } from '@/raptorjob/application/adapters/useMapRaptorJobPayload';
import type { CreateRaptorJobVariables } from '@/raptorjob/application/adapters/useCreateRaptorSingleDayJob';
import type { PostApiV3RaptorJobsCreateSingleDayJobMutationBody } from '@/libs/api-rest/generated/raptor-jobs/raptor-jobs';
import { RAPTOR_JOB_QUERY_KEYS } from '../hooks/useRaptorJob';

/**
 * Custom query key prefixes for ticket-related queries that use manually-set keys
 */
const TICKET_QUERY_PREFIXES = ['raptorJobTickets', 'jobTicketERP'];

export const invalidateJobTicketsQueries = (queryClient: ReturnType<typeof useQueryClient>, jobId: number): void => {
  queryClient.invalidateQueries({
    queryKey: getGetApiV3RaptorJobsJobIdTicketsQueryKey(jobId),
    refetchType: 'active'
  });

  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey;
      if (!Array.isArray(queryKey) || queryKey.length === 0) {
        return false;
      }

      const firstKey = queryKey[0];
      const isTicketQuery = typeof firstKey === 'string' && (TICKET_QUERY_PREFIXES.includes(firstKey) || firstKey.includes('Tickets'));
      return isTicketQuery && queryKey[1] === jobId;
    },
    refetchType: 'active'
  });
};

type RaptorJobsListParams = {
  page: number;
  pageSize: number;
  searchValue?: string;
  enabled?: boolean;
};

export const useGetRaptorJobs = ({ page, pageSize, searchValue, enabled = true }: RaptorJobsListParams) => {
  const isOrvalEnabled = useFeatureFlag(FEATURE_FLAGS.ORVAL_ENABLED);

  const { data, isLoading, error, refetch, ...rest } = useGetApiV3RaptorJobs(
    {
      Page: page + 1,
      PageSize: pageSize,
      // Adjust filter fields as needed by backend search semantics
      Filters: searchValue ? `(Name|JobNumber|CustomerName)@=${searchValue}` : undefined
    },
    {
      query: {
        enabled: enabled && isOrvalEnabled,
        staleTime: 1000 * 60 * 10,
        queryKey: ['raptorJobs', page, pageSize, searchValue]
      }
    }
  );

  useShowErrorSnackbar(error, 'Failed to load jobs');

  return {
    ...rest,
    data: data?.data,
    isLoading,
    error,
    refetch
  };
};

export const useGetRaptorJob = (id: number, queryParams?: { legacy?: boolean }) => {
  const isOrvalEnabled = useFeatureFlag(FEATURE_FLAGS.ORVAL_ENABLED);

  const { data, isLoading, error, refetch, ...rest } = useGetApiV3RaptorJobsId(id, queryParams, {
    query: {
      enabled: !!id && isOrvalEnabled,
      staleTime: 1000 * 60 * 10
    }
  });

  useShowErrorSnackbar(error, 'Failed to load job');

  return {
    ...rest,
    data: data?.data,
    isLoading,
    error,
    refetch
  };
};

// export const useCreateRaptorJob = () => {
//   const { showSnackbar } = useSnackbar();
//   const queryClient = useQueryClient();

//   const { mutate, mutateAsync, isPending, error, ...rest } = usePostApiV3RaptorJobs({
//     mutation: {
//       onSuccess: (response) => {
//         queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsQueryKey() });
//         const jobName = (response?.data as { name?: string } | undefined)?.name || 'Job';
//         showSnackbar('Success', `Raptor job "${jobName}" created successfully`, 'success');
//       },
//       onError: (err) => {
//         const message = err instanceof Error ? err.message : 'Failed to create job';
//         showSnackbar('Error', message, 'error');
//         console.error('Failed to create job:', err);
//       }
//     }
//   });

//   return {
//     ...rest,
//     createJob: mutate,
//     createJobAsync: mutateAsync,
//     isCreating: isPending,
//     error
//   };
// };

export const useUpdateRaptorJob = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, error, ...rest } = usePutApiV3RaptorJobsId({
    mutation: {
      onSuccess: (response, variables) => {
        const id = variables?.id;
        if (id) {
          queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsIdQueryKey(id) });
          invalidateJobTicketsQueries(queryClient, id);
        }
        queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsQueryKey() });
        const jobName = (response?.data as { name?: string } | undefined)?.name || 'Job';
        showSnackbar('Success', `Raptor job "${jobName}" updated successfully`, 'success');
      },
      onError: (err) => {
        const responseData =
          err && typeof err === 'object' && 'response' in err ? (err as { response?: { data?: unknown } }).response?.data : undefined;
        const message = extractRestApiErrorMessage(responseData, err instanceof Error ? err.message : 'Failed to update job');
        showSnackbar('Error', message, 'error');
      }
    }
  });

  return {
    ...rest,
    updateJob: mutate,
    updateJobAsync: mutateAsync,
    isUpdating: isPending,
    error
  };
};

export const useUpdateRaptorJobStatus = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, error, ...rest } = usePatchApiV3RaptorJobsIdUpdateStatus({
    mutation: {
      onSuccess: (_response, variables) => {
        const id = variables?.id;
        if (id) {
          queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsIdQueryKey(id) });
          invalidateJobTicketsQueries(queryClient, id);
        }
        queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsQueryKey() });
        showSnackbar('Success', 'Job status updated successfully', 'success');
      },
      onError: (err) => {
        const responseData =
          err && typeof err === 'object' && 'response' in err ? (err as { response?: { data?: unknown } }).response?.data : undefined;
        const message = extractRestApiErrorMessage(responseData, err instanceof Error ? err.message : 'Failed to update job status');
        showSnackbar('Error', message, 'error');
      }
    }
  });

  return {
    ...rest,
    updateJobStatus: mutate,
    updateJobStatusAsync: mutateAsync,
    isUpdating: isPending,
    error
  };
};

export const useCancelRaptorJob = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, error, ...rest } = useMutation({
    mutationFn: (id: number) => raptorJobApiService.cancelRaptorJob(id, true),
    onSuccess: (updated: RaptorJobDto) => {
      if (updated?.id) {
        queryClient.invalidateQueries({ queryKey: RAPTOR_JOB_QUERY_KEYS.raptorJob(Number(updated.id)) });
        queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsIdQueryKey(Number(updated.id)) });
      }
      // Invalidate all RaptorJobs list queries (including dashboard)
      queryClient.invalidateQueries({ queryKey: RAPTOR_JOB_QUERY_KEYS.raptorJobs });
      queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsQueryKey() });
      showSnackbar('Success', 'Raptor job canceled successfully', 'success');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to cancel job';
      showSnackbar('Error', message, 'error');
      console.error('Failed to cancel job:', err);
    }
  });

  return {
    ...rest,
    cancelJob: mutate,
    cancelJobAsync: mutateAsync,
    isCancelling: isPending,
    error
  };
};

export const useCreateSingleDayJob = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mapSingleDayPayload } = useMapRaptorJobPayload();

  const {
    mutate: baseMutate,
    mutateAsync: baseMutateAsync,
    isPending,
    error,
    ...rest
  } = usePostApiV3RaptorJobsCreateSingleDayJob({
    mutation: {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ['raptorJobs'] });
        const jobName = (response?.data as { name?: string } | undefined)?.name || 'Job';
        showSnackbar('Success', `Single day job "${jobName}" created successfully`, 'success');
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : 'Failed to create single day job';
        showSnackbar('Error', message, 'error');
        console.error('Failed to create single day job:', err);
      }
    }
  });

  const mutate = (vars: CreateRaptorJobVariables) => {
    try {
      const payload = mapSingleDayPayload(vars);
      return baseMutate({ data: payload as unknown as PostApiV3RaptorJobsCreateSingleDayJobMutationBody });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to create single day job';
      showSnackbar('Error', message, 'error');
      throw e;
    }
  };

  const mutateAsync = (vars: CreateRaptorJobVariables) => {
    try {
      const payload = mapSingleDayPayload(vars);
      return baseMutateAsync({ data: payload as unknown as PostApiV3RaptorJobsCreateSingleDayJobMutationBody });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to create single day job';
      showSnackbar('Error', message, 'error');
      return Promise.reject(e);
    }
  };

  return {
    ...rest,
    mutate,
    mutateAsync,
    isPending,
    error
  };
};
